import React, { useCallback, useMemo } from 'react';
import type { OtpDetails } from 'ehs-react';
import { Breakpoints, Button, intl, Select, useBreakpoint } from 'ehs-react';
import { useLoginCredentialsStore } from '../stores/loginCredentials.store';
import { PortletStoreApi } from '../App';

type OtpDetailsWithLabel = OtpDetails & {
  label: string;
};

const addLabel = function (otpDetails: OtpDetails): OtpDetailsWithLabel {
  let label: string = otpDetails.sendMethod;
  if (otpDetails.sendMethod === 'EMAIL') {
    label = intl('twofactorconfigportlet.view.authmethod.email.title');
  } else if (otpDetails.sendMethod === 'SMS') {
    label = intl('twofactorconfigportlet.view.authmethod.sms.title');
  } else if (otpDetails.sendMethod === 'TOTP') {
    label = intl('twofactorconfigportlet.view.authmethod.totp.title');
  }
  return {
    ...otpDetails,
    label: label
  };
};

export type TwoFactorSelectionAreaParameters = {
  onSubmit: (twoFactorMethod: OtpDetails) => void;
};
export function TwoFactorSelectionArea({
  onSubmit
}: TwoFactorSelectionAreaParameters): JSX.Element {
  const [
    viewState,
    availableTwoFactorMethods,
    selectedTwoFactorMethod,
    setSelectedTwoFactorMethod
  ] = useLoginCredentialsStore(state => [
    state.viewState,
    state.availableTwoFactorMethods,
    state.selectedTwoFactorMethod,
    state.setSelectedTwoFactorMethod
  ]);
  const availableTwoFactorMethodsWithLabel = availableTwoFactorMethods.map(
    addLabel
  );
  const selectedTwoFactorMethodWithLabel = selectedTwoFactorMethod
    ? addLabel(selectedTwoFactorMethod)
    : null;

  const isMobile = useBreakpoint(Breakpoints.MOBILE);

  const submitHandler = useCallback(() => {
    if (selectedTwoFactorMethod) {
      onSubmit(selectedTwoFactorMethod);
    }
  }, [onSubmit, selectedTwoFactorMethod]);

  const url = PortletStoreApi.getState().url;
  const isPatientPortal = useMemo(() => url.includes('patient-portal'), [url]);

  if (
    availableTwoFactorMethods.length <= 1 ||
    !selectedTwoFactorMethod ||
    viewState !== 'TWO_FACTOR_METHOD_SELECTION'
  ) {
    return <></>;
  }
  return (
    <div className="two-factor-selection-area progress-card-react">
      {isPatientPortal && (
        <div className="authentication-content-image-container">
          <img
            src="/o/LoginHook/images/authentication_wizard_otp.svg"
            role="none"
          />
        </div>
      )}
      <p className="font-16 s-font"> {intl('loginhook.twofactor.view')} </p>
      <Select<OtpDetailsWithLabel>
        variant="single"
        hideClearIcon={true}
        label={intl('loginhook.twofactor.view.select')}
        items={availableTwoFactorMethodsWithLabel}
        itemValueProp="sendMethod"
        itemLabelProp="label"
        value={selectedTwoFactorMethodWithLabel}
        onChange={selectedEntry =>
          selectedEntry && setSelectedTwoFactorMethod(selectedEntry)
        }
        autoFocus={true}
      />
      <div className={`content-button-bar ${isMobile ? 'mobile' : ''}`}>
        <Button
          variant="primary"
          text={intl('view.button.next')}
          onClick={submitHandler}
        />
      </div>
    </div>
  );
}
